import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../ui';
import {
  BoxIcon, ChevronRightIcon, ImageIcon, LinkIcon, ListIcon, LocationIcon, UserIcon, UsersIcon,
} from '../ui/icons';
import WorkerWarning from '../imports/WorkerWarning';

/**
 * The pages of Settings and of Administration. `hint` is the line under
 * each one on the section's home page.
 */
export const SECTIONS = {
  settings: {
    root: '/settings',
    label: 'shell.settings',
    links: [
      { to: '/settings/item-types', label: 'nav.itemTypes', hint: 'sections.itemTypesHint', icon: BoxIcon },
      { to: '/settings/locations', label: 'shell.locations', hint: 'sections.locationsHint', icon: LocationIcon },
      { to: '/settings/account', label: 'sections.account', hint: 'sections.accountHint', icon: UserIcon },
    ],
  },
  admin: {
    root: '/admin',
    label: 'shell.administration',
    links: [
      { to: '/admin/accounts', label: 'nav.accounts', hint: 'sections.accountsHint', icon: UsersIcon },
      { to: '/admin/backlog', label: 'nav.backlog', hint: 'sections.backlogHint', icon: ListIcon },
      { to: '/admin/ai', label: 'nav.aiConfiguration', hint: 'sections.aiHint', icon: ImageIcon },
      { to: '/admin/agents', label: 'nav.completionAgentsDeprecated', hint: 'sections.agentsHint', icon: LinkIcon },
    ],
  },
};

/**
 * Settings or Administration around one of its pages: the list of the
 * section's pages on the left (≥ 768 px), the page on the right. On a phone
 * the list is the section's home page, and each page leads back to it
 * through its breadcrumb (shown there as a single "‹ back" link).
 */
export function SectionLayout({ section, children }) {
  const { t } = useTranslation();
  const config = SECTIONS[section];
  return (
    <div className="section-layout">
      <nav className="section-layout__nav" aria-label={t(config.label)}>
        <p className="section-layout__title" aria-hidden="true">
          <Link to={config.root} tabIndex={-1}>{t(config.label)}</Link>
        </p>
        <ul>
          {config.links.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink to={to} className="section-layout__link">
                <Icon className="section-layout__icon" />
                <span>{t(label)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="section-layout__page">{children}</div>
    </div>
  );
}

/** /settings, /admin: the section's pages as a list, each with what it holds. */
export function SectionHome({ section }) {
  const { t } = useTranslation();
  const config = SECTIONS[section];
  return (
    <>
      <PageHeader title={t(config.label)} />
      {section === 'admin' && <WorkerWarning />}
      <ul className="section-home">
        {config.links.map(({ to, label, hint, icon: Icon }) => (
          <li key={to}>
            <Link to={to} className="section-home__link">
              <span className="section-home__icon"><Icon size={20} /></span>
              <span className="section-home__text">
                <span className="section-home__label">{t(label)}</span>
                <span className="visually-hidden">{t('ui.colon')} </span>
                <span className="section-home__hint">{t(hint)}</span>
              </span>
              <ChevronRightIcon className="section-home__chevron" />
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

/** The breadcrumb's first step on a section's pages. */
export const sectionCrumb = (t, section) => ({ label: t(SECTIONS[section].label), to: SECTIONS[section].root });
